import { useTranslations } from 'next-intl';
import { type FC, useEffect } from 'react';
import InfiniteScroll from 'react-infinite-scroll-component';
import { BaseLineClean } from '@/app/components/icons/BaseLineClean';
import { BaseLineMessage } from '@/app/components/icons/BaseLineMessage';
import { BaseLineMessageLight } from '@/app/components/icons/BaseLineMessageLight';
import { Popover, PopoverContent, PopoverTrigger } from '@/app/components/ui/popover';
import { cn, reportEvent } from '@/app/utils';
import { EmptyState } from './components/EmptyState';
import { SingleMessageItem } from './components/SingleMessageItem';
import { SystemNotificationDetailDialog } from './components/SystemNotificationDetailDialog';
import { SystemNotificationItem } from './components/SystemNotificationItem';
import { useNotifications } from './hook/use-notifications';

interface PCNotificationsProps {
  popoverOpen: boolean;
  onPopoverOpenChange: (open: boolean) => void;
  theme?: 'light' | 'dark';
  /** 覆盖触发器按钮样式（如侧边栏需要与其它 icon 对齐时） */
  triggerClassName?: string;
}

export const PCNotifications: FC<PCNotificationsProps> = ({
  popoverOpen,
  onPopoverOpenChange,
  theme = 'dark',
  triggerClassName,
}) => {
  const t = useTranslations();
  const {
    handleMarkAllAsRead,
    handleClickSysNotification,
    openSysNotificationId,
    handleCloseDetail,
    notifications,
    hasMore,
    activeTab,
    changeActiveTab,
    unreadCount,
    initNotifications,
    getNotifications,
    setOpenSysNotificationId,
  } = useNotifications();

  useEffect(() => {
    if (popoverOpen) {
      initNotifications();
    }
  }, [popoverOpen]);

  const renderNotificationLists = () => {
    return (
      <div className="flex flex-col gap-2 overflow-y-auto p-2" id="scrollableContainer">
        <InfiniteScroll
          dataLength={notifications.length}
          next={getNotifications}
          scrollableTarget="scrollableContainer"
          hasMore={hasMore}
          loader={<p className="text-center leading-[140px]">Loading...</p>}
          scrollThreshold={0.3}
        >
          {notifications.map((notification) => {
            const { type, system, interaction } = notification;
            if (type === 'system' && system) {
              return (
                <SystemNotificationItem
                  key={system?.notification?.id}
                  data={system}
                  onNotificationClick={handleClickSysNotification}
                  setOpenSysNotificationId={setOpenSysNotificationId}
                />
              );
            }
            if (type === 'interaction' && interaction) {
              return (
                <SingleMessageItem
                  key={interaction.id}
                  data={interaction}
                  onNotificationClick={handleClickSysNotification}
                />
              );
            }
            return null;
          })}
        </InfiniteScroll>
      </div>
    );
  };

  return (
    <>
      {/* PC端 */}
      <Popover
        open={popoverOpen}
        onOpenChange={(open) => {
          onPopoverOpenChange(open);
        }}
      >
        <PopoverTrigger asChild>
          <div
            className={cn(
              'relative flex aspect-square size-9 cursor-pointer items-center justify-center rounded-full bg-system-white12 text-system-white hover:bg-system-white24 md:hidden md:border-none',
              triggerClassName,
            )}
            onClick={() => {
              reportEvent('notification_button_click', {
                open: !popoverOpen,
                type: activeTab === 'system' ? 'platform_messages' : 'interactive_massages',
              });
            }}
          >
            {theme === 'dark' ? <BaseLineMessage className="text-white text-xl" /> : <BaseLineMessageLight />}
            {unreadCount > 0 && (
              <div className="-right-1 absolute top-0 flex h-[14px] min-w-[14px] items-center justify-center rounded-full bg-system-error1 px-1 text-[11px] text-white">
                {unreadCount > 99 ? '99+' : unreadCount}
              </div>
            )}
          </div>
        </PopoverTrigger>
        <PopoverContent
          sideOffset={8}
          align="end"
          className="flex max-h-[400px] w-[400px] flex-col gap-0 rounded-12 border border-system-white12 bg-system-bg02 p-0 [box-shadow:0px_20px_60px_0px_rgba(0,0,0,0.32)] md:hidden lg:w-[404px] lg:gap-3"
        >
          <div className="mx-4 flex items-center justify-between border-system-white08 border-b py-3">
            <div className="flex h-8 cursor-pointer items-center overflow-hidden rounded-8 bg-system-black24 text-system-white64 text-xs">
              <div
                className={cn('rounded-8 px-3 py-2', activeTab === 'interaction' && 'bg-[#39404F] text-white')}
                onClick={() => changeActiveTab('interaction')}
              >
                {t('notifications.interactiveTitle')}
              </div>
              <div
                className={cn('rounded-8 px-3 py-2', activeTab === 'system' && 'bg-[#39404F] text-white')}
                onClick={() => changeActiveTab('system')}
              >
                {t('notifications.platformTitle')}
              </div>
            </div>
            <BaseLineClean className="size-5 cursor-pointer" onClick={handleMarkAllAsRead} />
          </div>
          {hasMore || notifications.length ? renderNotificationLists() : <EmptyState activeTab={activeTab} />}
        </PopoverContent>
      </Popover>
      {openSysNotificationId && (
        <SystemNotificationDetailDialog
          openSysNotificationId={openSysNotificationId}
          handleCloseDetail={handleCloseDetail}
        />
      )}
    </>
  );
};
